/**
 * Billing Hooks
 *
 * React Query hooks for billing and subscription operations.
 * Provides data fetching and mutation hooks for subscription management.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelSubscription,
  createCheckoutSession,
  createPortalSession,
  getAvailablePlans,
  getPublicPlans,
  getBillingHistory,
  getSubscriptionDetails,
  getSubscriptionStatus,
  reactivateSubscription,
  retryPayment,
  validateDiscountCode,
} from "../services/billingService";
import type {
  ActionResponse,
  BillingHistoryResponse,
  CheckoutRequest,
  CheckoutResponse,
  PortalRequest,
  PortalResponse,
  SubscriptionPlan,
  SubscriptionStatusResponse,
  UserSubscription,
  ValidateDiscountRequest,
  ValidateDiscountResponse,
} from "../types/billing";

/**
 * Query keys for billing-related queries
 */
export const billingKeys = {
  all: ["billing"] as const,
  status: () => [...billingKeys.all, "status"] as const,
  details: () => [...billingKeys.all, "details"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  publicPlans: () => [...billingKeys.all, "publicPlans"] as const,
  history: (limit?: number) => [...billingKeys.all, "history", limit] as const,
};

/**
 * Hook to get subscription status (lightweight check)
 */
export function useSubscriptionStatus() {
  return useQuery<SubscriptionStatusResponse, Error>({
    queryKey: billingKeys.status(),
    queryFn: getSubscriptionStatus,
    staleTime: 30 * 1000, // 30 seconds - subscription status can change
    retry: 1,
  });
}

/**
 * Hook to get detailed subscription information
 */
export function useSubscriptionDetails() {
  return useQuery<UserSubscription, Error>({
    queryKey: billingKeys.details(),
    queryFn: getSubscriptionDetails,
    staleTime: 30 * 1000,
    retry: 1,
  });
}

/**
 * Hook to get available subscription plans
 */
export function useAvailablePlans() {
  return useQuery<SubscriptionPlan[], Error>({
    queryKey: billingKeys.plans(),
    queryFn: getAvailablePlans,
    staleTime: 5 * 60 * 1000, // 5 minutes - plans don't change often
  });
}

/**
 * Hook to get all public plans (including free) for the marketing page.
 * No auth required. Uses longer staleTime since this is public data.
 */
export function usePublicPlans() {
  return useQuery<SubscriptionPlan[], Error>({
    queryKey: billingKeys.publicPlans(),
    queryFn: getPublicPlans,
    staleTime: 10 * 60 * 1000, // 10 minutes - public plans change rarely
  });
}

/**
 * Hook to get billing history
 */
export function useBillingHistory(limit: number = 50) {
  return useQuery<BillingHistoryResponse, Error>({
    queryKey: billingKeys.history(limit),
    queryFn: () => getBillingHistory(limit),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a checkout session
 */
export function useCreateCheckoutSession() {
  return useMutation<CheckoutResponse, Error, CheckoutRequest>({
    mutationFn: createCheckoutSession,
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    },
  });
}

/**
 * Hook to create a portal session
 */
export function useCreatePortalSession() {
  return useMutation<PortalResponse, Error, PortalRequest>({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      // Redirect to Stripe Customer Portal
      window.location.href = data.portal_url;
    },
  });
}

/**
 * Hook to retry a failed payment
 */
export function useRetryPayment() {
  const queryClient = useQueryClient();

  return useMutation<ActionResponse, Error>({
    mutationFn: retryPayment,
    onSuccess: () => {
      // Invalidate subscription queries to refresh status
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation<ActionResponse, Error, boolean>({
    mutationFn: (atPeriodEnd: boolean = true) =>
      cancelSubscription(atPeriodEnd),
    onSuccess: () => {
      // Invalidate subscription queries to refresh status
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/**
 * Hook to reactivate a subscription
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation<ActionResponse, Error>({
    mutationFn: reactivateSubscription,
    onSuccess: () => {
      // Invalidate subscription queries to refresh status
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/**
 * Hook to validate a discount code
 */
export function useValidateDiscountCode() {
  return useMutation<ValidateDiscountResponse, Error, ValidateDiscountRequest>({
    mutationFn: validateDiscountCode,
  });
}
